/**
 * scansSlice — FE #124 / backend#312 + #313
 *
 * Redux state for the Scan History feature. Drives:
 *   - `/scans` route's ScanHistoryScreen
 *   - Dashboard recovery banner (selectUnfinishedCount)
 *   - `[+ Add New Item]` sheet's "Resume scan (N unfinished)" row
 *   - Top-bar 🕒 history icon (no state needed — just navigates)
 *
 * State shape mirrors notificationsSlice (the closest precedent —
 * list + pagination + per-id lookup + light polling):
 *   - byId: { [file_record_id]: ScanHistoryItem }
 *   - listOrder: number[]          (file_record_ids in current sort)
 *   - pagination: total + hasMore + nextOffset
 *   - loadingList / loadingMore / loadingDetail per surface
 *   - pollingById: which IN_PROGRESS scans are being polled
 *   - lastPolledAt: per-id timestamp of last poll
 *
 * See USJ.md J2.B4 v2 for the UX this powers.
 */

import {
  createAsyncThunk,
  createSlice,
  createSelector,
  type PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { api } from '../../api/remembrApi';
import type {
  ScanDetailResponse,
  ScanHistoryItem,
  ScanHistoryListParams,
  ScanHistoryResponse,
  ScanStatus,
} from '../../api/types';

/* ──────────────────────────────────────────────────────────────────────
 * State shape
 * ────────────────────────────────────────────────────────────────────── */

interface ScansState {
  byId: Record<number, ScanHistoryItem>;
  /** Order of `file_record_id`s in the current sort. The history page
   *  iterates `listOrder` and looks up `byId[id]` per row. */
  listOrder: number[];
  total: number;
  hasMore: boolean;
  nextOffset: number;
  loadingList: boolean;
  loadingMore: boolean;
  /** Per-scan detail fetch state (the heavier `/users/scans/{id}` call).
   *  Resume button taps trigger this to seed SegmentScreen. */
  loadingDetailById: Record<number, boolean>;
  /** Per-id discard-in-flight flag. Used to disable the row mid-delete
   *  and to roll back if the BE rejects. */
  discardingById: Record<number, boolean>;
  /** Per-id retry-in-flight flag for the [Retry] CTA on FAILED rows. */
  retryingById: Record<number, boolean>;
  /** Polling state — tracks which IN_PROGRESS scans we're currently
   *  polling so we can de-dup and stop on status flip. */
  pollingIds: number[];
  /** Last successful refresh timestamp. Drives "Pull-to-refresh fired
   *  too recently" rate limiting on the FE side (3s grace). */
  lastListLoadedAt: number | null;
  listError: string | null;
}

const initialState: ScansState = {
  byId: {},
  listOrder: [],
  total: 0,
  hasMore: false,
  nextOffset: 0,
  loadingList: false,
  loadingMore: false,
  loadingDetailById: {},
  discardingById: {},
  retryingById: {},
  pollingIds: [],
  lastListLoadedAt: null,
  listError: null,
};

/* ──────────────────────────────────────────────────────────────────────
 * Thunks
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Normalize a raw BE response into the canonical `ScanHistoryResponse`
 * shape the slice + screen expect. Bridges the gap between today's
 * production BE (which returns `items_count` / `verified_count` /
 * `bbox_count` / no status / no pagination wrapper) and the spec'd
 * shape from backend#312 (status enum + detected/saved counts +
 * pagination). Once backend#312 ships, this normalizer becomes a
 * no-op for the new fields and only the pagination defaults stay
 * active for safety.
 */
/** Normalize a single scan row from either the LIST endpoint (BE#312
 *  shape) or the legacy DETAIL endpoint (counts only, no status enum).
 *  Both paths return a `ScanHistoryItem` with the union-typed `status`
 *  field populated — never undefined.
 *
 *  Exported because `refreshScan` polls the detail endpoint and needs
 *  the same normalization (otherwise updates land with `status:
 *  undefined` and ScanRow's `statusVisuals` returns undefined → crash
 *  on `v.label`). */
export function normalizeScanRow(s: any): ScanHistoryItem {
  const items_count = s.items_count ?? 0;
  const verified_count = s.verified_count ?? 0;
  const bbox_count = s.bbox_count ?? 0;
  const detected_count = s.detected_count ?? items_count;
  const saved_count = s.saved_count ?? verified_count;
  // Status inference for legacy responses:
  //   verified > 0           → COMPLETED (some items confirmed)
  //   bbox > 0 && verified=0 → READY_TO_SAVE (detected, unconfirmed)
  //   bbox = 0 && items > 0  → READY_TO_SAVE (no bboxes? still has items)
  //   all zero               → FAILED (scan yielded nothing — could
  //                            also be IN_PROGRESS but we have no
  //                            signal to tell from legacy response)
  let inferredStatus: ScanStatus = 'FAILED';
  if (verified_count > 0) inferredStatus = 'COMPLETED';
  else if (bbox_count > 0 || items_count > 0)
    inferredStatus = 'READY_TO_SAVE';
  const status: ScanStatus = (s.status as ScanStatus) ?? inferredStatus;
  return {
    file_record_id: s.file_record_id,
    filename: s.filename ?? null,
    thumbnail_url: s.thumbnail_url ?? null,
    room_context: s.room_context ?? null,
    status,
    detected_count,
    saved_count,
    last_event_at: s.last_event_at ?? s.created_at,
    error_code: s.error_code ?? null,
    created_at: s.created_at,
  } as ScanHistoryItem;
}

function normalizeScansResponse(raw: any, limit: number): ScanHistoryResponse {
  const rawScans = Array.isArray(raw?.scans) ? raw.scans : [];
  const scans = rawScans.map(normalizeScanRow);
  // Pagination — use the BE's pagination object if present, otherwise
  // synthesize a "single page" envelope so the slice's hasMore logic
  // doesn't NPE.
  const total = raw?.pagination?.total ?? scans.length;
  const offset = raw?.pagination?.offset ?? 0;
  const has_next = raw?.pagination?.has_next ?? scans.length === limit;
  return {
    scans,
    pagination: {
      total,
      limit,
      offset,
      total_pages: Math.ceil(total / Math.max(1, limit)),
      current_page: Math.floor(offset / Math.max(1, limit)) + 1,
      has_next,
      has_previous: offset > 0,
    },
  };
}

/** Fetch the first page (mode='replace') or append the next page
 *  (mode='append'). Default 'replace'. The replace path also resets
 *  `byId` to drop any rows the BE has deleted server-side since
 *  the last load (so a server-side discard from another device
 *  reflects in our list on next refresh). */
export const loadScans = createAsyncThunk<
  { data: ScanHistoryResponse; mode: 'replace' | 'append' },
  { params?: ScanHistoryListParams; mode?: 'replace' | 'append' } | undefined,
  { rejectValue: string }
>('scans/loadScans', async (arg, { rejectWithValue, getState }) => {
  const mode = arg?.mode ?? 'replace';
  const state = getState() as RootState;
  const params: ScanHistoryListParams = {
    limit: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...arg?.params,
  };
  if (mode === 'append') {
    params.offset = state.scans.nextOffset;
  }
  try {
    const rawAny = (await api.getUserScans(params)) as any;
    const data = normalizeScansResponse(rawAny, params.limit ?? 20);
    return { data, mode };
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail ??
        err?.message ??
        'Failed to load scans',
    );
  }
});

/** Lightweight refresh of a single scan — used by the polling loop
 *  on IN_PROGRESS rows to update `detected_count` + status without
 *  re-fetching the whole list. Does NOT include the nested
 *  inventory_items; that's `loadScanDetail`. */
export const refreshScan = createAsyncThunk<
  ScanHistoryItem,
  number,
  { rejectValue: string }
>('scans/refreshScan', async (fileRecordId, { rejectWithValue }) => {
  try {
    // The list endpoint with the right filter is the cheapest way:
    //   GET /users/scans?limit=1&offset=0 + a "file_record_id" filter
    // would be ideal — but the BE doesn't accept id filter. So we
    // fetch the detail endpoint and discard the nested items.
    const detail = await api.getUserScan(fileRecordId);
    // Strip the heavy inventory_items + source_image_url to fit the
    // ScanHistoryItem shape (extends-not-replace; cheap shallow pick).
    // ALSO run through `normalizeScanRow` so the detail endpoint's
    // legacy shape (no `status` enum, counts only) gets the same
    // status-inference treatment as the list endpoint. Without this,
    // polling fires → state.byId[id].status becomes `undefined` →
    // ScanRow's `statusVisuals(undefined)` returns undefined → crash
    // on `v.label`. Caught in journey2-resume-flow smoke.
    const { inventory_items, source_image_url, ...summary } = detail;
    return normalizeScanRow(summary);
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail ??
        err?.message ??
        'Failed to refresh scan',
    );
  }
});

/** Fetch a single scan's full detail (incl. nested inventory_items).
 *  Called when the user taps a row → resume into SegmentScreen. */
export const loadScanDetail = createAsyncThunk<
  ScanDetailResponse,
  number,
  { rejectValue: string }
>('scans/loadScanDetail', async (fileRecordId, { rejectWithValue }) => {
  try {
    return await api.getUserScan(fileRecordId);
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail ??
        err?.message ??
        'Failed to load scan detail',
    );
  }
});

/** Discard a scan (`DELETE /users/scans/{id}`). Optimistic — row is
 *  removed from listOrder immediately; rolled back on failure. */
export const discardScan = createAsyncThunk<
  number,
  number,
  { rejectValue: string; state: RootState }
>('scans/discardScan', async (fileRecordId, { rejectWithValue }) => {
  try {
    await api.deleteScan(fileRecordId);
    return fileRecordId;
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail ??
        err?.message ??
        'Failed to discard scan',
    );
  }
});

/* ──────────────────────────────────────────────────────────────────────
 * Slice
 * ────────────────────────────────────────────────────────────────────── */

const scansSlice = createSlice({
  name: 'scans',
  initialState,
  reducers: {
    /** Optimistic remove — pulled out as a reducer so the discardScan
     *  thunk can call it before the network round-trip + roll back
     *  via `restoreScan` on failure. */
    optimisticallyRemoveScan: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      delete state.byId[id];
      state.listOrder = state.listOrder.filter((x) => x !== id);
      state.total = Math.max(0, state.total - 1);
    },
    /** Roll-back companion to optimisticallyRemoveScan. */
    restoreScan: (
      state,
      action: PayloadAction<{ id: number; row: ScanHistoryItem; idx: number }>,
    ) => {
      const { id, row, idx } = action.payload;
      state.byId[id] = row;
      // Insert at the original index so sort order is preserved.
      if (!state.listOrder.includes(id)) {
        state.listOrder.splice(idx, 0, id);
      }
      state.total += 1;
    },
    /** Polling lifecycle — the polling helper (in the screen) calls
     *  these to register/unregister itself. The slice just tracks
     *  which ids are being polled to avoid duplicates. */
    startPollingScan: (state, action: PayloadAction<number>) => {
      if (!state.pollingIds.includes(action.payload)) {
        state.pollingIds.push(action.payload);
      }
    },
    stopPollingScan: (state, action: PayloadAction<number>) => {
      state.pollingIds = state.pollingIds.filter((x) => x !== action.payload);
    },
    /** Clear any error banner. */
    clearListError: (state) => {
      state.listError = null;
    },
  },
  extraReducers: (builder) => {
    /* loadScans */
    builder
      .addCase(loadScans.pending, (state, action) => {
        if (action.meta.arg?.mode === 'append') {
          state.loadingMore = true;
        } else {
          state.loadingList = true;
        }
        state.listError = null;
      })
      .addCase(loadScans.fulfilled, (state, action) => {
        const { data, mode } = action.payload;
        state.loadingList = false;
        state.loadingMore = false;
        state.total = data.pagination.total;
        state.hasMore = data.pagination.has_next;
        state.nextOffset = data.pagination.offset + data.scans.length;
        state.lastListLoadedAt = Date.now();
        if (mode === 'replace') {
          state.byId = {};
          state.listOrder = [];
        }
        for (const scan of data.scans) {
          state.byId[scan.file_record_id] = scan;
          if (!state.listOrder.includes(scan.file_record_id)) {
            state.listOrder.push(scan.file_record_id);
          }
        }
      })
      .addCase(loadScans.rejected, (state, action) => {
        state.loadingList = false;
        state.loadingMore = false;
        state.listError = action.payload ?? 'Failed to load scans';
      });

    /* refreshScan — polling update */
    builder.addCase(refreshScan.fulfilled, (state, action) => {
      const scan = action.payload;
      state.byId[scan.file_record_id] = scan;
      // Status flipped to a terminal state? Stop polling automatically.
      const terminal: ScanStatus[] = [
        'READY_TO_SAVE',
        'COMPLETED',
        'FAILED',
        'CANCELLED',
      ];
      if (terminal.includes(scan.status)) {
        state.pollingIds = state.pollingIds.filter(
          (x) => x !== scan.file_record_id,
        );
      }
    });

    /* loadScanDetail — for the Resume / SegmentScreen path */
    builder
      .addCase(loadScanDetail.pending, (state, action) => {
        state.loadingDetailById[action.meta.arg] = true;
      })
      .addCase(loadScanDetail.fulfilled, (state, action) => {
        const detail = action.payload;
        state.loadingDetailById[detail.file_record_id] = false;
        // Update the summary row too — the detail call returns the
        // canonical state-of-the-scan-right-now, which the list may
        // have stale.
        const { inventory_items, source_image_url, ...summary } = detail;
        state.byId[detail.file_record_id] = summary as ScanHistoryItem;
      })
      .addCase(loadScanDetail.rejected, (state, action) => {
        state.loadingDetailById[action.meta.arg] = false;
      });

    /* discardScan — optimistic; we remove first, then roll back if
     *  the BE rejects. The thunk's caller (the row in
     *  ScanHistoryScreen) is responsible for dispatching the
     *  `optimisticallyRemoveScan` reducer first, then awaiting
     *  the thunk and rolling back on failure. */
    builder.addCase(discardScan.pending, (state, action) => {
      state.discardingById[action.meta.arg] = true;
    });
    builder.addCase(discardScan.fulfilled, (state, action) => {
      state.discardingById[action.payload] = false;
      // The optimistic-remove already cleared it from byId + listOrder.
      // Polling cleanup too in case the user discarded a LIVE scan.
      state.pollingIds = state.pollingIds.filter(
        (x) => x !== action.payload,
      );
    });
    builder.addCase(discardScan.rejected, (state, action) => {
      state.discardingById[action.meta.arg] = false;
    });
  },
});

export const {
  optimisticallyRemoveScan,
  restoreScan,
  startPollingScan,
  stopPollingScan,
  clearListError,
} = scansSlice.actions;

export default scansSlice.reducer;

/* ──────────────────────────────────────────────────────────────────────
 * Selectors
 * ────────────────────────────────────────────────────────────────────── */

export const selectScansState = (state: RootState) => state.scans;

export const selectScansLoading = (state: RootState) =>
  state.scans.loadingList;
export const selectScansLoadingMore = (state: RootState) =>
  state.scans.loadingMore;
export const selectScansHasMore = (state: RootState) => state.scans.hasMore;
export const selectScansError = (state: RootState) => state.scans.listError;
export const selectScansTotal = (state: RootState) => state.scans.total;

/** Ordered list of scans for the history page render. */
export const selectScansList = createSelector(
  [(state: RootState) => state.scans.listOrder, (state: RootState) => state.scans.byId],
  (order, byId): ScanHistoryItem[] =>
    order.map((id) => byId[id]).filter(Boolean),
);

/** Single scan lookup (for SegmentScreen resume path + per-row state). */
export const selectScanById =
  (id: number) =>
  (state: RootState): ScanHistoryItem | undefined =>
    state.scans.byId[id];

/** Per-row pending flags for the discard / retry CTAs. */
export const selectIsDiscarding =
  (id: number) =>
  (state: RootState): boolean =>
    state.scans.discardingById[id] === true;

/** "Unfinished" = IN_PROGRESS or READY_TO_SAVE. Drives:
 *    - Dashboard recovery banner (show when count > 0)
 *    - [+ Add New Item] sheet's "Resume scan (N unfinished)" row
 *    - History page summary line
 *
 *  Memoized so the banner doesn't re-render on every poll-tick of an
 *  unrelated row. */
export const selectUnfinishedScans = createSelector(
  [selectScansList],
  (list): ScanHistoryItem[] =>
    list.filter(
      (s) => s.status === 'IN_PROGRESS' || s.status === 'READY_TO_SAVE',
    ),
);

export const selectUnfinishedCount = createSelector(
  [selectUnfinishedScans],
  (unfinished): number => unfinished.length,
);

/** Status-bucketed view for the history page summary line +
 *  visual section breaks ("─── Completed ───"). */
export const selectScansByStatus = createSelector(
  [selectScansList],
  (list) => {
    const inProgress: ScanHistoryItem[] = [];
    const readyToSave: ScanHistoryItem[] = [];
    const failed: ScanHistoryItem[] = [];
    const completed: ScanHistoryItem[] = [];
    const cancelled: ScanHistoryItem[] = [];
    for (const s of list) {
      switch (s.status) {
        case 'IN_PROGRESS':
          inProgress.push(s);
          break;
        case 'READY_TO_SAVE':
          readyToSave.push(s);
          break;
        case 'FAILED':
          failed.push(s);
          break;
        case 'COMPLETED':
          completed.push(s);
          break;
        case 'CANCELLED':
          cancelled.push(s);
          break;
      }
    }
    return { inProgress, readyToSave, failed, completed, cancelled };
  },
);

/** IN_PROGRESS scan ids that should be polled. Used by ScanHistoryScreen's
 *  polling effect to spin up + tear down the 2s tick per row. */
export const selectInProgressIds = createSelector(
  [selectScansList],
  (list) =>
    list.filter((s) => s.status === 'IN_PROGRESS').map((s) => s.file_record_id),
);
